import log from "loglevel";

import { CsatChatState, CsatScale, CSAT_SCALES } from "./store/definitions";
import { isValidCanonicalScore } from "./components/csat/csatScales";
import { reportClientError } from "./utils/reportClientError";

/*
 * csatHandler (v2 CSAT pull model). Fala com o synccx (base = REACT_APP_CSAT_URL), NÃO com o webchat-api.
 * Auth = header `X-Csat-Key` (a key HMAC vem do /initWebchat, guardada no localStorage). Best-effort:
 * NUNCA lança, NUNCA loga key/score/comment; ausência de env/key ⇒ no-op (o card não renderiza/envia).
 *
 * ⚠️ Assunção do GET (a validar na F8 com o synccx): o `conversationSid` viaja na QUERY (o GET não tem
 * body); a key vai no header. O backend recomputa HMAC(secret, sid) p/ validar. Se eles quiserem o sid
 * em outro lugar, só muda a montagem da URL aqui.
 */

const csatBaseUrl = (): string => (process.env.REACT_APP_CSAT_URL || "").trim();

const REQUEST_TIMEOUT_MS = 8000;
const DEFAULT_GET_RETRY_DELAYS = [2000, 5000]; // corrida do AFK
const DEFAULT_POST_RETRY_DELAYS = [2000]; // 1 retry p/ 429/5xx

/* Seed do store (o `status` é local, setado pelo componente). */
export type CsatSeed = Omit<CsatChatState, "status">;
export type SubmitResult = "submitted" | "readonly" | "error" | "noop";

const isObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        return await fetch(url, { ...init, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

/* Valida o corpo do GET antes de semear o store (S5): shape mínimo + expiresAt parseável. */
function toSeed(body: unknown): CsatSeed | null {
    if (!isObject(body) || body.requested !== true) {
        return null;
    }
    const expiresAt = typeof body.expiresAt === "string" ? body.expiresAt : "";
    if (!expiresAt || Number.isNaN(Date.parse(expiresAt))) {
        return null;
    }
    const scale: CsatScale = CSAT_SCALES.includes(body.scale as CsatScale) ? (body.scale as CsatScale) : "stars";
    return {
        requested: true,
        /*
         * Tradução na fronteira: o valor do FIO é do synccx, o tipo interno é nosso.
         *
         * O synccx grava `"humano"`, em português — e até 2026-09-12 esta linha só aceitava
         * `"human"`, então toda pesquisa de canal humano era normalizada para `"ai"`. Sem sintoma,
         * porque nada no widget lê `channel` para decidir; armadilha para o primeiro que lesse.
         *
         * As duas grafias são aceitas de propósito, para proteger contra qualquer um dos lados
         * mudar depois. O que não for nenhuma delas continua sendo `"ai"`.
         */
        channel: body.channel === "humano" || body.channel === "human" ? "human" : "ai",
        scale,
        allowEdit: body.allowEdit === true,
        expiresAt,
        answered: body.answered === true,
        ...(typeof body.score === "number" ? { score: body.score } : {}),
        ...(typeof body.comment === "string" ? { comment: body.comment } : {})
    };
}

/*
 * Consulta a pesquisa pendente (no atendimento, quando o sinal acende, ou na despedida). Retorna o
 * seed (requested:true) ou null (sem pesquisa / sem env-key / 410 / 403 / erro). Quando a pesquisa
 * não vem, o motivo vai para a telemetria segundo `avisarPesquisaPerdida`. No AFK a linha pode ainda não existir (requested:false) →
 * retenta nos delays informados. 410/403 são terminais (não retenta). Nunca lança.
 */
/*
 * Por que a pesquisa não pôde ser mostrada. Motivo FIXO, sem SID e sem chave: vai para a
 * telemetria, que é canal de operação. Spec em `csatHandler.test.ts` ("telemetria de quando a
 * pesquisa não pode ser mostrada").
 */
type MotivoDaPesquisaPerdida = "sem_chave" | "chave_recusada" | "pesquisa_ausente" | "expirada" | "rede";

/*
 * Chave ausente ou recusada é anomalia em qualquer momento. Os outros motivos só são anomalia
 * DURANTE o atendimento, onde o widget só busca porque o synccx acendeu o sinal: na despedida de
 * uma conversa da IA sem gatilho, não haver pesquisa é o normal, e avisar ali seria ruído.
 */
function avisarPesquisaPerdida(
    motivo: MotivoDaPesquisaPerdida,
    noAtendimento: boolean,
    aindaImporta: () => boolean
): void {
    // Quem pediu a busca saiu da tela (a faixa desmontou quando a conversa encerrou): o contexto dela
    // já não vale, e quem avisa, se for o caso, é a busca da despedida.
    if (!aindaImporta()) {
        return;
    }
    if (motivo === "sem_chave" || motivo === "chave_recusada" || noAtendimento) {
        reportClientError("csat_pending_failed", { reason: motivo });
    }
}

export async function getPendingCsat(
    {
        conversationSid,
        csatKey,
        noAtendimento = false,
        aindaImporta = () => true
    }: {
        conversationSid?: string;
        csatKey?: string;
        /** A busca acontece durante o atendimento (sinal aceso), e não na despedida. */
        noAtendimento?: boolean;
        /** `false` quando quem pediu já saiu da tela — aí a desistência não vira aviso. */
        aindaImporta?: () => boolean;
    },
    retryDelaysMs: number[] = DEFAULT_GET_RETRY_DELAYS
): Promise<CsatSeed | null> {
    const base = csatBaseUrl();
    // Sem a URL no build ou sem conversa não há o que buscar: é configuração, não falha.
    if (!base || !conversationSid) {
        return null;
    }
    if (!csatKey) {
        avisarPesquisaPerdida("sem_chave", noAtendimento, aindaImporta);
        return null;
    }

    const url = `${base}/api/csat/pending?conversationSid=${encodeURIComponent(conversationSid)}`;
    const attempts = 1 + retryDelaysMs.length;

    let motivo: MotivoDaPesquisaPerdida | undefined;
    for (let i = 0; i < attempts; i += 1) {
        let terminal = false;
        try {
            const res = await fetchWithTimeout(url, {
                method: "GET",
                headers: { Accept: "application/json", "X-Csat-Key": csatKey },
                cache: "no-store"
            });
            if (res.status === 410 || res.status === 403) {
                terminal = true; // pós-janela / key inválida → sem card, sem retry
                motivo = res.status === 403 ? "chave_recusada" : "expirada";
            } else if (res.ok) {
                const body = await res.json().catch(() => null);
                const seed = toSeed(body);
                if (seed) {
                    return seed;
                }
                // requested:false (ou malformado): pode ser a corrida do AFK → cai no retry
                motivo = "pesquisa_ausente";
            } else {
                motivo = "rede";
            }
        } catch (e) {
            motivo = "rede";
            log.debug("getPendingCsat: request falhou (best-effort, seguindo)");
        }

        if (terminal || i >= retryDelaysMs.length) {
            avisarPesquisaPerdida(motivo ?? "rede", noAtendimento, aindaImporta);
            return null;
        }
        await delay(retryDelaysMs[i]);
    }

    return null;
}

/*
 * Envia a nota. Guard S1 antes do POST (int 1..5; thumbs só {1,5}) — não manda inválido. Header X-Csat-Key,
 * body enxuto {conversationSid, score, comment?} (comment truncado em 500). 200 ⇒ submitted; 410/409/403/400
 * ⇒ readonly (terminal); 429/5xx/rede ⇒ 1 retry, depois error (desiste em silêncio). Nunca lança/loga.
 */
/*
 * Auditoria de observabilidade (2026-08-28): a falha definitiva devolvia "error" e pronto — a nota
 * que o apostador deu se perdia e nós não sabíamos que o CSAT estava quebrado. A pesquisa inteira
 * pode estar sem coletar nada, e a única pista seria a Visão Geral vazia no portal.
 *
 * 🔒 PII: o `comment` é texto livre do apostador (aparece CPF ali o tempo todo). SÓ o status HTTP
 * entra na telemetria — nunca o comentário, nunca a nota, nunca o sid.
 */
function falhaDefinitiva(status: number): "error" {
    reportClientError("csat_submit_failed", { reason: `envio da nota falhou (status ${status})` });
    return "error";
}

export async function submitCsat(
    {
        conversationSid,
        csatKey,
        score,
        comment,
        scale
    }: { conversationSid?: string; csatKey?: string; score: number; comment?: string; scale: CsatScale },
    retryDelaysMs: number[] = DEFAULT_POST_RETRY_DELAYS
): Promise<SubmitResult> {
    const base = csatBaseUrl();
    if (!base || !csatKey || !conversationSid) {
        return "noop";
    }
    if (!isValidCanonicalScore(score, scale)) {
        return "error";
    }

    const trimmed = typeof comment === "string" ? comment.slice(0, 500) : undefined;
    const body = JSON.stringify({ conversationSid, score, ...(trimmed ? { comment: trimmed } : {}) });
    const attempts = 1 + retryDelaysMs.length;

    /*
     * ⚠️ Declarado FORA do loop de propósito. O `return` depois do loop precisa do último status, e
     * uma `let status` de escopo do bloco não chega lá — o que o TS resolvia silenciosamente para o
     * `window.status` global (tipo `string`) do lib.dom. Só o `yarn build` pegou; a suíte não pega
     * porque o babel apaga os tipos.
     */
    let ultimoStatus = 0;

    for (let i = 0; i < attempts; i += 1) {
        try {
            const res = await fetchWithTimeout(`${base}/api/csat`, {
                method: "POST",
                headers: { Accept: "application/json", "Content-Type": "application/json", "X-Csat-Key": csatKey },
                body
            });
            ultimoStatus = res.status;
        } catch (e) {
            ultimoStatus = 0; // rede/timeout → transiente
        }

        if (ultimoStatus === 200) {
            return "submitted";
        }
        if (ultimoStatus === 410 || ultimoStatus === 409) {
            return "readonly"; // desfechos legítimos (fora da janela / já respondeu) — sem retry, sem alerta
        }
        if (ultimoStatus === 403 || ultimoStatus === 400) {
            /*
             * Terminais como os de cima, MAS por bug nosso: 403 = a `csatKey` foi rejeitada (o
             * `CSAT_KEY_SECRET` do synccx e o do webchat saíram de sincronia), 400 = o payload foi
             * recusado. Nos dois o apostador vê a tela de agradecimento e NADA é gravado — é o jeito
             * mais provável da pesquisa parar de coletar sem ninguém notar.
             */
            reportClientError("csat_submit_failed", { reason: `envio da nota recusado (status ${ultimoStatus})` });
            return "readonly";
        }
        // 429 / 5xx / 0 (rede) → transiente
        if (i >= retryDelaysMs.length) {
            return falhaDefinitiva(ultimoStatus);
        }
        await delay(retryDelaysMs[i]);
    }

    return falhaDefinitiva(ultimoStatus);
}
